/**
 * The policy page's tabs and the URL hash that selects one.
 *
 * The page read only `#findings` and fell back to Overview for anything else,
 * so "Back to policy" from a suggestion (`#suggestions`) landed on Overview.
 * Every tab is addressable, and switching tabs writes the hash back, so the
 * browser's Back button from a finding or a suggestion returns to the tab it
 * was opened from.
 *
 * The document itself is not a tab. Reading the PDF is an action (View
 * document) that opens it in a preview over the page, so no PDF loads until
 * someone asks for it. `#content` — the hash of the tab it replaced — and
 * `#document` still address it: they open the preview over Overview.
 *
 * @module components/regulation/policy/policyPageTabs
 */

export const POLICY_PAGE_TABS = [
  "overview",
  "mappings",
  "findings",
  "suggestions",
  "versions",
  "activity",
  "comments",
] as const;

export type PolicyPageTab = (typeof POLICY_PAGE_TABS)[number];

/** Hashes that ask for the document preview rather than a tab. */
export const POLICY_DOCUMENT_HASHES = ["document", "content"] as const;

const isPolicyPageTab = (value: string): value is PolicyPageTab =>
  (POLICY_PAGE_TABS as ReadonlyArray<string>).includes(value);

/**
 * TanStack Router's `location.hash` carries no leading `#` — that is what the
 * page passes. A `#` is tolerated as well, so `window.location.hash` reads the
 * same way and neither caller can be "fixed" into a mismatch.
 */
const bareHash = (hash: string | null | undefined): string => (hash ?? "").replace(/^#/, "");

/** The tab a hash selects; Overview for none, an unknown one, or a document request. */
export function policyTabFromHash(hash: string | null | undefined): PolicyPageTab {
  const value = bareHash(hash);
  return isPolicyPageTab(value) ? value : "overview";
}

/** Whether a hash asks for the document preview (`#document`, or the old `#content` tab). */
export const policyDocumentRequested = (hash: string | null | undefined): boolean =>
  (POLICY_DOCUMENT_HASHES as ReadonlyArray<string>).includes(bareHash(hash));

/** The hash a tab writes: none for Overview, the page's default. */
export const policyTabHash = (tab: string): string =>
  isPolicyPageTab(tab) && tab !== "overview" ? tab : "";

export interface PolicyPageView {
  /** The tab to show. */
  readonly tab: PolicyPageTab;
  /** The document preview was asked for by the URL. */
  readonly documentRequested: boolean;
  /** Whether the Record updates tab is in the list. */
  readonly showRecordUpdates: boolean;
}

/**
 * What the page shows for a hash, given what is known about the document.
 *
 * Record updates is housekeeping Inspector proposes on some documents and not
 * others; a tab that only ever says "none here" is noise on most of them. It
 * is listed once the document has one (decided ones included), and while the
 * count is still loading only if the URL asked for it — so "Back to policy"
 * from a suggestion lands on it. A request for it on a document that turns
 * out to have none falls back to Overview rather than a tab with no trigger.
 */
export function policyPageView(
  hash: string | null | undefined,
  recordUpdateCount: number | null,
): PolicyPageView {
  const requested = policyTabFromHash(hash);
  const showRecordUpdates =
    recordUpdateCount === null ? requested === "suggestions" : recordUpdateCount > 0;
  return {
    tab: requested === "suggestions" && !showRecordUpdates ? "overview" : requested,
    documentRequested: policyDocumentRequested(hash),
    showRecordUpdates,
  };
}
